import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Check } from 'lucide-react';
import { formatIban, isValidIban } from './utils';

export default function StepSepa({ form, setField, F, title, tenantName, invalid }) {
  const sepa = form.sepa;
  const ibanEntered = sepa.iban.replace(/\s/g, '').length > 0;
  const ibanValid = isValidIban(sepa.iban);
  const ibanError = (ibanEntered && !ibanValid) || invalid('sepa.iban');

  return (
    <div className="space-y-5" data-testid="public-registration-step-sepa">
      <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>

      <div>
        <Label>IBAN *</Label>
        <div className="relative">
          <Input
            value={formatIban(sepa.iban)}
            onChange={(e) => setField('sepa', 'iban', e.target.value.toUpperCase())}
            placeholder="DE02 3002 0900 0106 5310 65"
            className={`font-mono pr-10 ${ibanError ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : ''}`}
            data-testid="public-registration-sepa-iban"
          />
          {ibanEntered && !ibanValid && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-lg leading-none">⊘</span>
          )}
          {ibanEntered && ibanValid && (
            <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />
          )}
        </div>
        {ibanError && (
          <p className="text-sm text-red-600 mt-1" data-testid="public-registration-sepa-iban-error">Gib eine gültige IBAN ein.</p>
        )}
      </div>

      <p className="text-slate-800">
        Ich ermächtige die {tenantName || 'Musikschule'}, Zahlungen von meinem Konto mittels Sepa-Lastschrift einzuziehen.
      </p>

      <div>
        <p className="font-medium text-slate-900 mb-2">SEPA-Lastschriftmandat erteilen?</p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-sepa-acknowledge-row">
          <Checkbox
            checked={sepa.mandate_acknowledged}
            onCheckedChange={(v) => setField('sepa', 'mandate_acknowledged', !!v)}
            className={invalid('sepa.mandate') ? 'border-red-500' : ''}
            data-testid="public-registration-sepa-acknowledge"
          />
          <span className={invalid('sepa.mandate') ? 'text-red-600 font-medium' : 'text-slate-800'}>Hiermit erteile ich ein SEPA-Lastschriftmandat *</span>
        </label>
      </div>

      <div>
        <p className="font-medium text-slate-900 mb-2">Kontoinhaber:in abweichend?</p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-sepa-different-row">
          <Switch
            checked={sepa.different_holder}
            onCheckedChange={(v) => setField('sepa', 'different_holder', !!v)}
            data-testid="public-registration-sepa-different-toggle"
          />
          <span className="text-slate-800">Abweichende:r Kontoinhaber:in</span>
        </label>
        {sepa.different_holder ? (
          <div className="mt-3 max-w-md">
            <Label className={invalid('sepa.account_holder') ? 'text-red-600' : ''}>Kontoinhaber:in *</Label>
            <Input
              value={sepa.account_holder}
              onChange={(e) => setField('sepa', 'account_holder', e.target.value)}
              placeholder="Vor- und Nachname"
              className={invalid('sepa.account_holder') ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : ''}
              data-testid="public-registration-sepa-holder"
            />
          </div>
        ) : (
          (form.payer.first_name || form.payer.last_name) && (
            <p className="text-sm text-slate-500 mt-2" data-testid="public-registration-sepa-holder-info">
              Kontoinhaber:in: {form.payer.first_name} {form.payer.last_name} (Zahlungspflichtige:r)
            </p>
          )
        )}
      </div>

      <p className="text-right text-sm text-slate-500">* Pflichtfeld</p>
    </div>
  );
}
